import { forwardRef, useId } from "react";
import { XIcon } from "lucide-react";

import { cn } from "src/shadcn/lib/utils";
import { Field, FieldLabel, FieldContent } from "src/primitives/Field";
import { withIconSize } from "src/utils/withIconSize";
import { useMultiEmailState } from "./useMultiEmailState";
import { pluralizeEmail, formatDuplicateWarning } from "./utils";
import type { MultiEmailInputProps } from "./types";

const CHIP_BASE =
  "flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground";
const CHIP_INVALID =
  "border border-destructive text-destructive bg-destructive/10";
const CHIPS_CONTAINER =
  "flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-input bg-transparent bg-clip-padding px-2.5 py-1 text-base md:text-sm transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50";
const CHIPS_ERROR =
  "border-destructive ring-3 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40";

export const MultiEmailInput = forwardRef<
  HTMLInputElement,
  MultiEmailInputProps
>((props, ref) => {
  const {
    label,
    placeholder = "",
    helpText,
    error,
    disabled = false,
    required = false,
    maxHeight = 200,
    counter,
    filterInvalidEmails,
    prefix,
    suffix,
    labelProps,
    className,
  } = props;
  const id = useId();
  const {
    isCounterVisible,
    validCount,
    visibleEmails,
    hiddenCount,
    inputValue,
    setInputValue,
    handleKeyDown,
    handlePaste,
    handleFocus,
    handleBlur,
    handleChipRemove,
    isFilterLinkVisible,
    handleFilterInvalidEmails,
    duplicateEmails,
  } = useMultiEmailState(props);

  return (
    <Field className={cn("flex flex-col gap-1.5", className)}>
      {(label || isCounterVisible) && (
        <div className="flex items-center justify-between">
          {label && (
            <FieldLabel htmlFor={id} {...labelProps}>
              {label}
              {required && (
                <span aria-hidden="true" className="text-destructive">
                  {" *"}
                </span>
              )}
            </FieldLabel>
          )}
          {isCounterVisible && (
            <span className="text-xs text-muted-foreground">
              {validCount}{" "}
              {typeof counter === "object" && counter?.label
                ? counter.label
                : pluralizeEmail(validCount)}
            </span>
          )}
        </div>
      )}
      <div
        className={cn(
          CHIPS_CONTAINER,
          !!error && CHIPS_ERROR,
          disabled && "pointer-events-none opacity-50"
        )}
        style={{ maxHeight: `${maxHeight}px`, overflowY: "auto" }}
      >
        {prefix && (
          <div className="shrink-0 text-muted-foreground">
            {withIconSize(prefix, "size-4")}
          </div>
        )}
        {visibleEmails.map(email => (
          <span
            key={email.value}
            className={cn(CHIP_BASE, !email.valid && CHIP_INVALID)}
          >
            {email.label}
            {!disabled && (
              <button
                type="button"
                tabIndex={-1}
                className="-ms-0.5 rounded-xs opacity-50 hover:opacity-100"
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleChipRemove(email.value)}
                aria-label={`Remove ${email.label}`}
              >
                <XIcon className="size-3 pointer-events-none" />
              </button>
            )}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="flex h-[calc(--spacing(5.25))] items-center rounded-sm bg-secondary px-1.5 text-xs font-medium text-secondary-foreground">
            +{hiddenCount} more
          </span>
        )}
        <input
          ref={ref}
          id={id}
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={props.value?.length ? "" : placeholder}
          disabled={disabled}
          className="min-w-16 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
        />
        {suffix && (
          <div className="shrink-0 text-muted-foreground">
            {withIconSize(suffix, "size-4")}
          </div>
        )}
      </div>
      <FieldContent>
        {!!error && (
          <p className="text-[0.8rem] text-destructive">
            {error}
            {isFilterLinkVisible && (
              <button
                type="button"
                className="ml-1 cursor-pointer font-semibold underline"
                onClick={handleFilterInvalidEmails}
              >
                {filterInvalidEmails?.label ??
                  "Click here to remove invalid emails."}
              </button>
            )}
          </p>
        )}
        {!!helpText && (
          <p className="text-[0.8rem] text-muted-foreground">{helpText}</p>
        )}
        {duplicateEmails.length > 0 && (
          <p className="text-[0.8rem] text-amber-600 dark:text-amber-400">
            {formatDuplicateWarning(duplicateEmails)}
          </p>
        )}
      </FieldContent>
    </Field>
  );
});
MultiEmailInput.displayName = "MultiEmailInput";
